import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { fetchSendEmailCode, getVerifyCode } from "../pages/service/fetch";

interface ModalProps {
  show: boolean;
  onHide: Function;
  email: string;
  onVerifyPass: Function;
}

export const VerfiyModal = (props: ModalProps) => {
  const { show, onHide, email, onVerifyPass } = props;
  const [code, setCode] = useState<string>("");
  const [verifying, setVerifying] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (sendingCode || verifying) return;
    if (show) {
      sendVerificationCode(email);
    }
  }, [show]);

  const sendVerificationCode = async (email: string) => {
    setSendingCode(true);
    try {
      await fetchSendEmailCode(email);
      setCountdown(90);
      setCodeError("");
    } catch (err: any) {
      setCodeError(err.message || "Failed to send code.");
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length < 6) {
      return setCodeError("Please enter a valid verification code.");
    }

    setVerifying(true);
    try {
      const res = await getVerifyCode("email", email, code);
      if (res?.status === "1") {
        onVerifyPass();
      } else {
        setCodeError("Invalid verification code.");
      }
    } catch (err: any) {
      setCodeError(err.message || "Verification failed.");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    let timer: any;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  return (
    <Modal open={show} onClose={() => onHide()} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{ 
        backgroundColor: "background.paper",
        borderRadius: 2,
        boxShadow: 24,
        width: 400,
        p: 3,
        outline: "none"
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Email Verification</Typography>
          <Button onClick={() => onHide()} sx={{ minWidth: 0 }}>×</Button>
        </Box>
        
        <Typography sx={{ mb: 3 }}>
          We sent code to <strong>{email}</strong>.
        </Typography>
        
        <TextField
          fullWidth
          variant="outlined"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter code"
          inputProps={{ maxLength: 6 }}
          sx={{ mb: 2 }}
        />
        
        {codeError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {codeError}
          </Typography>
        )}
        
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleVerifyCode}
            disabled={verifying}
            sx={{ backgroundColor: "#6b8aff", "&:hover": { backgroundColor: "#5a79e0" } }}
          >
            {verifying ? <CircularProgress size={20} color="inherit" /> : "Verify & Submit"}
          </Button>
          
          <Button
            variant="text"
            onClick={() => sendVerificationCode(email)}
            disabled={countdown > 0}
            sx={{ color: "#6b8aff" }}
          >
            {sendingCode ? (
              <CircularProgress size={20} color="inherit" />
            ) : countdown > 0 ? (
              `Resend in ${countdown}s`
            ) : (
              "Resend Code"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};